import { useState } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TraffickingCaseRecord {
  id: number;
  person_id: number;
  agency_id: number;
  case_details: string | null;
  created_by: number | null;
  created_at: string;
  author_first_name?: string;
  author_last_name?: string;
  customFields?: Record<string, string>;
}

export interface TraffickingCaseFormData {
  caseDetails?: string;
  customFields?: Record<string, string>;
}

interface TraffickingCaseFormProps {
  initial?: TraffickingCaseRecord;
  onSubmit: (data: TraffickingCaseFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TraffickingCaseForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: TraffickingCaseFormProps) {
  const [caseDetails, setCaseDetails] = useState(
    initial?.case_details ?? "",
  );
  const [traffickingType, setTraffickingType] = useState(
    initial?.customFields?.trafficking_type ?? "",
  );
  const [servicesProvided, setServicesProvided] = useState(
    initial?.customFields?.services_provided ?? "",
  );
  const [referralsMade, setReferralsMade] = useState(
    initial?.customFields?.referrals_made ?? "",
  );

  function handleSubmit() {
    const customFields: Record<string, string> = {};
    if (traffickingType) customFields.trafficking_type = traffickingType;
    if (servicesProvided) customFields.services_provided = servicesProvided;
    if (referralsMade) customFields.referrals_made = referralsMade;

    onSubmit({
      caseDetails: caseDetails || undefined,
      customFields:
        Object.keys(customFields).length > 0 ? customFields : undefined,
    });
  }

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-foreground">
        {initial ? "Edit Trafficking Case" : "New Trafficking Case"}
      </h4>

      <Input
        label="Trafficking Type"
        value={traffickingType}
        onChange={(e) => setTraffickingType(e.target.value)}
        placeholder="e.g. Labor, Sex, Both..."
      />

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Case Details
        </label>
        <textarea
          value={caseDetails}
          onChange={(e) => setCaseDetails(e.target.value)}
          rows={4}
          placeholder="Enter case details (text or JSON)..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Services Provided
        </label>
        <textarea
          value={servicesProvided}
          onChange={(e) => setServicesProvided(e.target.value)}
          rows={3}
          placeholder="Services provided to the victim..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Referrals Made
        </label>
        <textarea
          value={referralsMade}
          onChange={(e) => setReferralsMade(e.target.value)}
          rows={3}
          placeholder="Referrals made to other agencies or services..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          {initial ? "Update Case" : "Create Case"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
